import React, { useState, useEffect } from "react";
import logo from "../assets/smalllogo.png";

/**
 * Attractive full-screen loading splash.
 *
 * Props:
 *  - appName (string)  -> text shown before dots, default "TrackLeads"
 *  - accent (string)    -> accent color, default "#ffd54f"
 */
const AppLoadingSplash = ({ appName = "TrackLeads", accent = "#ffd54f" }) => {
	const [dots, setDots] = useState("");

	useEffect(() => {
		const interval = setInterval(() => {
			setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
		}, 450);
		return () => clearInterval(interval);
	}, []);

	return (
		<div
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 9999,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				background:
					"radial-gradient(circle at 50% 35%, #1e293b 0%, #0f172a 55%, #0a0f1c 100%)",
				overflow: "hidden",
			}}
		>
			{/* ambient particles */}
			<div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
				{[...Array(6)].map((_, i) => (
					<div
						key={i}
						style={{
							position: "absolute",
							top: `${15 + i * 14}%`,
							left: `${10 + ((i * 37) % 80)}%`,
							width: 3,
							height: 3,
							borderRadius: "50%",
							background: accent,
							opacity: 0.25,
							animation: `app-splash-drift ${4 + i}s ease-in-out infinite`,
							animationDelay: `${i * 0.4}s`,
						}}
					/>
				))}
			</div>

			{/* soft glow behind logo */}
			<div
				style={{
					position: "absolute",
					width: 320,
					height: 320,
					borderRadius: "50%",
					background: `radial-gradient(circle, ${accent}33 0%, transparent 70%)`,
					animation: "app-splash-glow 2.4s ease-in-out infinite",
				}}
			/>

			{/* logo */}
			<div
				style={{
					position: "relative",
					width: 84,
					height: 84,
					borderRadius: "22px",
					background: "rgba(255,255,255,0.06)",
					backdropFilter: "blur(6px)",
					border: "1px solid rgba(255,255,255,0.1)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					boxShadow: `0 0 40px ${accent}22, inset 0 0 20px rgba(255,255,255,0.03)`,
					animation: "app-splash-float 2.6s ease-in-out infinite",
					marginBottom: 26,
				}}
			>
				<img
					src={logo}
					alt={`${appName} logo`}
					style={{
						width: 46,
						height: 46,
						objectFit: "contain",
						filter: `drop-shadow(0 0 8px ${accent}44)`,
					}}
				/>
				{/* rotating ring around logo */}
				<div
					style={{
						position: "absolute",
						inset: -8,
						borderRadius: "26px",
						border: `2px solid transparent`,
						borderTopColor: accent,
						borderRightColor: `${accent}55`,
						animation: "app-splash-spin 1.1s linear infinite",
					}}
				/>
				{/* second slower ring, opposite direction */}
				<div
					style={{
						position: "absolute",
						inset: -14,
						borderRadius: "30px",
						border: `1px solid ${accent}22`,
						borderBottomColor: `${accent}66`,
						animation: "app-splash-spin-reverse 2.2s linear infinite",
					}}
				/>
			</div>

			{/* app name */}
			<div
				style={{
					position: "relative",
					fontSize: 22,
					fontWeight: 700,
					letterSpacing: "0.5px",
					color: "#ffffff",
					display: "flex",
					alignItems: "baseline",
					fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
					backgroundImage: `linear-gradient(90deg, #fff 0%, #fff 40%, ${accent} 50%, #fff 60%, #fff 100%)`,
					backgroundSize: "200% 100%",
					WebkitBackgroundClip: "text",
					WebkitTextFillColor: "transparent",
					animation: "app-splash-shimmer 2.8s ease-in-out infinite",
				}}
			>
				{appName}
				<span style={{ color: accent, width: 24, textAlign: "left", WebkitTextFillColor: accent }}>
					{dots}
				</span>
			</div>

			<div
				style={{
					marginTop: 6,
					fontSize: 12.5,
					color: "rgba(255,255,255,0.4)",
					letterSpacing: "1.5px",
					textTransform: "uppercase",
				}}
			>
				Getting things ready
			</div>

			{/* progress bar */}
			<div
				style={{
					marginTop: 28,
					width: 180,
					height: 3,
					borderRadius: 4,
					background: "rgba(255,255,255,0.08)",
					overflow: "hidden",
					position: "relative",
				}}
			>
				<div
					style={{
						position: "absolute",
						height: "100%",
						width: "40%",
						borderRadius: 4,
						background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
						animation: "app-splash-bar 1.6s ease-in-out infinite",
					}}
				/>
			</div>

			<style>
				{`
					@keyframes app-splash-spin {
						from { transform: rotate(0deg); }
						to { transform: rotate(360deg); }
					}
					@keyframes app-splash-spin-reverse {
						from { transform: rotate(360deg); }
						to { transform: rotate(0deg); }
					}
					@keyframes app-splash-float {
						0%, 100% { transform: translateY(0px); }
						50% { transform: translateY(-6px); }
					}
					@keyframes app-splash-glow {
						0%, 100% { opacity: 0.6; transform: scale(1); }
						50% { opacity: 1; transform: scale(1.15); }
					}
					@keyframes app-splash-bar {
						0% { left: -40%; }
						100% { left: 100%; }
					}
					@keyframes app-splash-shimmer {
						0% { background-position: 200% 0; }
						100% { background-position: -200% 0; }
					}
					@keyframes app-splash-drift {
						0%, 100% { transform: translateY(0px); opacity: 0.15; }
						50% { transform: translateY(-14px); opacity: 0.4; }
					}
				`}
			</style>
		</div>
	);
};

export default AppLoadingSplash;